import { queryOptions, useQuery } from '@tanstack/react-query';

import { QueryOptions, QueryRefetchInterval } from '@cast/shared-types';
import { KentFeatureConfig } from '@cast/types-kent';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';
import { mapKentFeatureConfigDtoToKentFeatureConfig } from 'features/kent/utils';

import { useCurrentOrganizationId } from '@cast/infrastructure-organization';

const KENT_CLUSTER_FEATURES_CONFIGS_QUERY_KEY = 'kent-cluster-features-configs';

export const getFeaturesConfigsQueryKey = (
  clusterId?: string,
  featureId?: string
) =>
  featureId !== undefined
    ? ([KENT_CLUSTER_FEATURES_CONFIGS_QUERY_KEY, clusterId, featureId] as const)
    : ([KENT_CLUSTER_FEATURES_CONFIGS_QUERY_KEY, clusterId] as const);

export const getFeaturesConfigsQueryOptions = ({
  organizationId,
  clusterId,
  featureId = '-',
  enabled,
  refetchInterval,
  staleTime,
}: {
  organizationId: string;
  clusterId: string;
  featureId?: string;
  enabled?: boolean;
  refetchInterval?: QueryRefetchInterval<KentFeatureConfig[]>;
  staleTime?: QueryOptions<KentFeatureConfig[]>['staleTime'];
}) =>
  queryOptions({
    queryKey: getFeaturesConfigsQueryKey(clusterId, featureId),
    queryFn: async () => {
      const { data } = await apiClient.kent.getFeaturesConfigs({
        organizationId,
        clusterId,
        featureId,
      });
      return (data.items || []).map(mapKentFeatureConfigDtoToKentFeatureConfig);
    },
    enabled: makeIsEnabled(!!clusterId, enabled),
    refetchInterval,
    staleTime,
  });

type GetFeaturesConfigsQueryArgs = QueryOptions<KentFeatureConfig[]> & {
  clusterId: string;
  featureId?: string;
};

export const useGetFeaturesConfigsQuery = ({
  clusterId,
  featureId = '-', // '-' means all features
  enabled,
  refetchInterval,
  staleTime,
}: GetFeaturesConfigsQueryArgs) => {
  const currentOrganizationId = useCurrentOrganizationId();

  return useQuery(
    getFeaturesConfigsQueryOptions({
      organizationId: currentOrganizationId,
      clusterId,
      featureId,
      enabled,
      refetchInterval,
      staleTime,
    })
  );
};
